/** Query hooks of the import screens (deliverable 3 onward): reads under `['import']`, one mutation per write. */
import {
  docsRevisions,
  type ImportDryRunV1,
  type ImportMappingV1,
  type ImportRunV1,
  importRunsApply,
  importRunsCancel,
  importRunsDryRun,
  importRunsGet,
  importRunsList,
  importRunsPlan,
  importRunsPutMapping,
  importRunsUndo,
} from '@codex/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../auth/client.js';
import { isActiveStatus } from '../model/progress-model.js';

export const importKey = ['import'] as const;
export const runsKey = [...importKey, 'runs'] as const;
export const runKey = (runId: string) => [...importKey, 'run', runId] as const;
export const planKey = (runId: string) => [...importKey, 'plan', runId] as const;
export const dryRunKey = (runId: string) => [...importKey, 'dry-run', runId] as const;

/** How often the run is polled while an upload or a job works on it. */
export const POLL_MS = 2000;

export function useRuns() {
  return useQuery({
    queryKey: runsKey,
    queryFn: async () => (await api(importRunsList)).items,
    retry: false,
  });
}

export function useRun(runId: string, enabled = true) {
  return useQuery({
    queryKey: runKey(runId),
    queryFn: () => api(importRunsGet, { params: { id: runId } }),
    enabled,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status !== undefined && isActiveStatus(status) ? POLL_MS : false;
    },
  });
}

/** The plan's databases and properties, once the run is `planned`. */
export function usePlan(runId: string, enabled: boolean) {
  return useQuery({
    queryKey: planKey(runId),
    queryFn: () => api(importRunsPlan, { params: { id: runId } }),
    enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** The dry run kept beside the `rv` it was made for (deliverable 12). */
export interface KeptReport {
  readonly report: ImportDryRunV1;
  readonly rv: number;
  /** The run's choice stamp at the request: a choice made since marks the report out of date. */
  readonly stamp: number;
}

export const choiceStampKey = (runId: string) => [...importKey, 'choice-stamp', runId] as const;

/** How many choices this page has made on the run (a counter in the query cache, never sent). */
export function useChoiceStamp(runId: string): number {
  return (
    useQuery<number>({
      queryKey: choiceStampKey(runId),
      queryFn: () => 0,
      enabled: false,
      initialData: 0,
      staleTime: Number.POSITIVE_INFINITY,
    }).data ?? 0
  );
}

export function useKeptReport(runId: string) {
  return useQuery<KeptReport | null>({
    queryKey: dryRunKey(runId),
    queryFn: () => null,
    enabled: false,
    initialData: null,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

function useRunWrite<V>(write: (variables: V) => Promise<ImportRunV1>, runId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (run) => {
      client.setQueryData(runKey(runId), run);
      void client.invalidateQueries({ queryKey: runsKey });
    },
  });
}

/** Cancels a run; the run's own query and the list are refreshed. */
export function useCancelRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api(importRunsCancel, { params: { id: runId } }),
    onSuccess: () => client.invalidateQueries({ queryKey: importKey }),
  });
}

export const usePutMapping = (runId: string) =>
  useRunWrite(
    (variables: { baseRv: number; mapping: ImportMappingV1 }) =>
      api(importRunsPutMapping, { params: { id: runId }, body: variables }),
    runId,
  );

/** The dry run: writes nothing, so the report is kept beside the run's `rv` at the request. */
export function useDryRun(runId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ rv, stamp }: { rv: number; stamp: number }): Promise<KeptReport> => ({
      report: await api(importRunsDryRun, { params: { id: runId } }),
      rv,
      stamp,
    }),
    onSuccess: (kept) => client.setQueryData(dryRunKey(runId), kept),
  });
}

/** Import: `apply {baseRv}`; the run becomes `applying` and the page follows its progress. */
export const useApply = (runId: string) =>
  useRunWrite(
    (variables: { baseRv: number }) =>
      api(importRunsApply, { params: { id: runId }, body: variables }),
    runId,
  );

/** Undo import: the run becomes `undoing`. */
export const useUndo = (runId: string) =>
  useRunWrite(() => api(importRunsUndo, { params: { id: runId } }), runId);

/** Undo for a run named at press time (History lists many runs). */
export function useUndoRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api(importRunsUndo, { params: { id: runId } }),
    onSuccess: (run) => {
      client.setQueryData(runKey(run.id), run);
      void client.invalidateQueries({ queryKey: runsKey });
    },
  });
}

/** The newest revisions of a document: the Tidy pill reads them for an `import` reason. */
export function useRevisions(docId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...importKey, 'revisions', docId],
    queryFn: async () =>
      (await api(docsRevisions, { params: { id: docId }, query: { limit: 20 } })).items,
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}
